import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Bed, MapPin, Users as UsersIcon, Image as ImageIcon, Save, Shield, ArrowUp, ArrowDown, KeyRound, Compass, ExternalLink, Activity, Mail, Eye } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("info");
  const tabs = [
    { v: "info", label: "Info / Programm" },
    { v: "photos", label: "Bilder" },
    { v: "members", label: "Mitglieder" },
    { v: "accommodations", label: "Übernachtung" },
    { v: "excursions", label: "Ausflugsziele" },
    { v: "addresses", label: "Adressen" },
    { v: "activity", label: "Aktivität" },
    { v: "security", label: "Sicherheit" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-page">
      <div className="mb-10">
        <p className="label-eyebrow mb-3">Verwaltung</p>
        <h1 className="font-serif text-5xl tracking-tight">Admin-Bereich</h1>
        <p className="text-[#4A4F46] mt-3">Inhalte pflegen – schnell und einfach.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E5DFD3] pb-3">
        {tabs.map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              tab === t.v ? "bg-[#48634A] text-white" : "bg-white border border-[#E5DFD3] text-[#4A4F46] hover:bg-[#F4EFE6]"
            }`}
            data-testid={`admin-tab-${t.v}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoEditor />}
      {tab === "photos" && <PhotosManager />}
      {tab === "members" && <MembersManager />}
      {tab === "accommodations" && <AccommodationsManager />}
      {tab === "excursions" && <ExcursionsManager />}
      {tab === "addresses" && <AddressesManager />}
      {tab === "activity" && <ActivityPanel />}
      {tab === "security" && <SecurityManager />}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, multiline = false }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      {multiline ? (
        <textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={testid}
        />
      )}
    </label>
  );
}

function InfoEditor() {
  const [info, setInfo] = useState(null);
  const [programText, setProgramText] = useState("");

  useEffect(() => {
    api.get("/info").then((r) => {
      const data = r.data;
      // Sicherstellen, dass 6 Link-Plätze existieren
      const links = Array.from({ length: 6 }, (_, i) => data.gallery_links?.[i] || { label: "", url: "", note: "" });
      setInfo({ ...data, gallery_links: links });
      setProgramText((data.program || []).join("\n"));
    });
  }, []);

  const save = async () => {
    const payload = {
      ...info,
      program: programText.split("\n").map((s) => s.trim()).filter(Boolean),
      gallery_links: (info.gallery_links || []).filter((l) => (l.label || "").trim() || (l.url || "").trim()),
    };
    const res = await api.put("/info", payload);
    const links = Array.from({ length: 6 }, (_, i) => res.data.gallery_links?.[i] || { label: "", url: "", note: "" });
    setInfo({ ...res.data, gallery_links: links });
    toast.success("Info gespeichert");
  };

  const updateLink = (idx, field, value) => {
    setInfo((s) => {
      const links = [...(s.gallery_links || [])];
      links[idx] = { ...links[idx], [field]: value };
      return { ...s, gallery_links: links };
    });
  };

  if (!info) return null;
  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Titel" value={info.title} onChange={(v) => setInfo({ ...info, title: v })} testid="info-title" />
        <Field label="Untertitel" value={info.subtitle} onChange={(v) => setInfo({ ...info, subtitle: v })} testid="info-subtitle" />
        <Field label="Datum" value={info.event_date} onChange={(v) => setInfo({ ...info, event_date: v })} testid="info-date" />
        <Field label="Zeit" value={info.event_time} onChange={(v) => setInfo({ ...info, event_time: v })} testid="info-time" />
        <Field label="Ort" value={info.location} onChange={(v) => setInfo({ ...info, location: v })} testid="info-location" />
        <Field label="Adresse" value={info.address} onChange={(v) => setInfo({ ...info, address: v })} testid="info-address" />
        <div className="md:col-span-2">
          <Field label="Begrüßungstext" value={info.description} onChange={(v) => setInfo({ ...info, description: v })} testid="info-description" multiline />
        </div>
        <div className="md:col-span-2">
          <Field label="Login-Untertitel (auf der Anmelde-Seite)" value={info.login_subtitle} onChange={(v) => setInfo({ ...info, login_subtitle: v })} testid="info-login-subtitle" multiline />
        </div>
        <Field label="Footer-Text" value={info.footer_text} onChange={(v) => setInfo({ ...info, footer_text: v })} testid="info-footer-text" />
        <div />
        <div className="md:col-span-2">
          <Field label="Programm (eine Zeile pro Punkt)" value={programText} onChange={setProgramText} testid="info-program" multiline />
        </div>
      </div>

      {/* Galerie-spezifische Texte & Links */}
      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Anfahrt-Seite</h3>
        <p className="text-sm text-[#4A4F46] mb-5">Längerer Infotext und optionales Video zur Anreise.</p>
        <div className="grid gap-5">
          <Field
            label="Infotext zur Anreise (mehrzeilig, längerer Text möglich)"
            value={info.directions_text || ""}
            onChange={(v) => setInfo({ ...info, directions_text: v })}
            testid="info-directions-text"
            multiline
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Video-Titel"
              value={info.directions_video_title || ""}
              onChange={(v) => setInfo({ ...info, directions_video_title: v })}
              testid="info-directions-video-title"
            />
            <Field
              label="Video-Link (Drive / YouTube / Vimeo / .mp4)"
              value={info.directions_video_url || ""}
              onChange={(v) => setInfo({ ...info, directions_video_url: v })}
              testid="info-directions-video-url"
            />
          </div>
          <p className="text-xs text-[#4A4F46] italic">
            Tipp: Bei Google Drive den Link auf „Jeder mit dem Link" stellen.
          </p>
        </div>
      </div>

      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Übernachtung-Seite</h3>
        <p className="text-sm text-[#4A4F46] mb-5">Eigenes Hintergrund-Bild für die Übernachtung-Seite (oben).</p>
        <BackgroundImageEditor info={info} onChange={(id) => setInfo({ ...info, accommodations_bg_id: id })} />
      </div>

      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Turniere-Seite</h3>
        <p className="text-sm text-[#4A4F46] mb-5">
          Überschrift, Infotext und die Google-Tabelle für die Turnier-Anmeldungen.
        </p>
        <div className="grid gap-5">
          <Field
            label="Überschrift (z. B. Turniere)"
            value={info.tournaments_title || ""}
            onChange={(v) => setInfo({ ...info, tournaments_title: v })}
            testid="info-tournaments-title"
          />
          <Field
            label="Infotext über der Tabelle (mehrzeilig)"
            value={info.tournaments_intro || ""}
            onChange={(v) => setInfo({ ...info, tournaments_intro: v })}
            testid="info-tournaments-intro"
            multiline
          />
          <Field
            label="Google-Sheet-URL (komplett aus der Adresszeile)"
            value={info.tournaments_sheet_url || ""}
            onChange={(v) => setInfo({ ...info, tournaments_sheet_url: v })}
            testid="info-tournaments-sheet-url"
          />
          <p className="text-xs text-[#4A4F46] italic">
            Wichtig: Im Google-Sheet auf <em>Teilen &rarr; Jeder mit dem Link &rarr; Bearbeiter</em> stellen,
            damit Familienmitglieder ihre Teilnahme direkt eintragen können.
          </p>
        </div>
      </div>

      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Abrechnung-2026-Seite</h3>
        <p className="text-sm text-[#4A4F46] mb-5">
          Überschrift, Infotext, Screenshot der Übersicht und die Google-Tabelle (nur zur Ansicht).
        </p>
        <div className="grid gap-5">
          <Field
            label="Überschrift (z. B. Abrechnung 2026)"
            value={info.abrechnung_title || ""}
            onChange={(v) => setInfo({ ...info, abrechnung_title: v })}
            testid="info-abrechnung-title"
          />
          <Field
            label="Infotext unter der Überschrift (mehrzeilig)"
            value={info.abrechnung_intro || ""}
            onChange={(v) => setInfo({ ...info, abrechnung_intro: v })}
            testid="info-abrechnung-intro"
            multiline
          />
          <Field
            label="Google-Sheet-URL (komplett aus der Adresszeile)"
            value={info.abrechnung_sheet_url || ""}
            onChange={(v) => setInfo({ ...info, abrechnung_sheet_url: v })}
            testid="info-abrechnung-sheet-url"
          />
          <p className="text-xs text-[#4A4F46] italic">
            Hinweis: Im Google-Sheet auf <em>Teilen &rarr; Jeder mit dem Link &rarr; Betrachter</em> stellen –
            die Familie soll nur schauen, nicht bearbeiten.
          </p>
          <div>
            <span className="label-eyebrow block mb-2">Screenshot der Übersicht</span>
            <AbrechnungImageEditor info={info} onChange={(id) => setInfo({ ...info, abrechnung_image_id: id })} />
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Galerie-Seite</h3>
        <p className="text-sm text-[#4A4F46] mb-5">Diese Texte und Links erscheinen auf der Galerie-Seite.</p>
        <div className="grid gap-5">
          <Field
            label="Galerie-Einleitungstext (oben)"
            value={info.gallery_intro || ""}
            onChange={(v) => setInfo({ ...info, gallery_intro: v })}
            testid="info-gallery-intro"
            multiline
          />
          <Field
            label="Benutzungshinweis (z. B. Anleitung, was hochladen)"
            value={info.gallery_usage || ""}
            onChange={(v) => setInfo({ ...info, gallery_usage: v })}
            testid="info-gallery-usage"
            multiline
          />
        </div>
      </div>

      <div className="border-t border-[#E5DFD3] pt-8">
        <h3 className="font-serif text-2xl tracking-tight mb-2">Externe Foto-Sammlungen (bis zu 6)</h3>
        <p className="text-sm text-[#4A4F46] mb-5">
          Hier kannst du Links zu z. B. <strong>web.de</strong>-Speicher, OneDrive- oder Google-Drive-Ordnern eintragen. Leere Zeilen werden ausgeblendet.
        </p>
        <div className="space-y-4">
          {(info.gallery_links || []).map((link, i) => (
            <div key={i} className="bg-[#FDFBF7] border border-[#E5DFD3] rounded-xl p-4 grid md:grid-cols-2 gap-3" data-testid={`gallery-link-row-${i}`}>
              <Field label={`Bezeichnung #${i + 1}`} value={link.label || ""} onChange={(v) => updateLink(i, "label", v)} testid={`gallery-link-label-${i}`} />
              <Field label="Link (URL)" value={link.url || ""} onChange={(v) => updateLink(i, "url", v)} testid={`gallery-link-url-${i}`} />
              <div className="md:col-span-2">
                <Field label="Hinweis (z. B. Passwort, Inhalt)" value={link.note || ""} onChange={(v) => updateLink(i, "note", v)} testid={`gallery-link-note-${i}`} multiline />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <button onClick={save} className="btn-primary rounded-xl px-6 py-3 inline-flex items-center gap-2" data-testid="info-save">
          <Save className="w-4 h-4" /> Alles speichern
        </button>
      </div>
    </div>
  );
}

function PhotosManager() {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("ebel_token");

  const load = () => api.get("/photos").then((r) => setPhotos(r.data));
  useEffect(() => {
    load();
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    try {
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      toast.success("Bild hochgeladen");
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    await load();
    toast.success("Bild entfernt");
  };

  return (
    <div className="space-y-8">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6">
        <div className="flex items-center gap-2 text-[#48634A] mb-4">
          <Upload className="w-4 h-4" />
          <span className="label-eyebrow">Neues Bild hochladen</span>
        </div>
        <Field label="Bildunterschrift (optional)" value={caption} onChange={setCaption} testid="photo-caption" />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onUpload}
          disabled={busy}
          className="mt-3 block text-sm file:btn-primary file:rounded-xl file:border-0 file:px-4 file:py-2 file:mr-3"
          data-testid="photo-file-input"
        />
        <p className="text-xs text-[#4A4F46] mt-2 italic">Mehrere Bilder auf einmal möglich (Cmd/Strg + Klick).</p>
      </div>

      <div>
        <div className="flex items-center gap-2 text-[#48634A] mb-4">
          <ImageIcon className="w-4 h-4" />
          <span className="label-eyebrow">Vorhandene Bilder ({photos.length})</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden border border-[#E5DFD3] bg-white aspect-square">
              <img src={`${API}/photos/${p.id}/download?auth=${token}`} alt={p.caption || ""} className="w-full h-full object-cover" />
              <button
                onClick={() => remove(p.id)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-[#B74134] hover:text-white text-[#1A1C19] rounded-full p-2 transition-colors opacity-0 group-hover:opacity-100"
                data-testid={`photo-delete-${p.id}`}
                title="Bild entfernen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MembersManager() {
  const [members, setMembers] = useState([]);
  const [name, setName] = useState("");

  const load = () => api.get("/members").then((r) => setMembers(r.data));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/members", { name: name.trim() });
    setName("");
    await load();
    toast.success("Mitglied hinzugefügt");
  };

  const remove = async (id) => {
    await api.delete(`/members/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Field label="Name oder Familie" value={name} onChange={setName} testid="member-add-name" />
        </div>
        <button onClick={add} className="btn-primary rounded-xl px-5 py-3 inline-flex items-center gap-2" data-testid="member-add-btn">
          <Plus className="w-4 h-4" /> Hinzufügen
        </button>
      </div>
      <ul className="space-y-2">
        {members.map((m) => (
          <li key={m.id} className="flex items-center justify-between bg-white border border-[#E5DFD3] rounded-xl px-5 py-3" data-testid={`member-item-${m.id}`}>
            <div className="flex items-center gap-3">
              <UsersIcon className="w-4 h-4 text-[#48634A]" />
              <span className="font-serif text-lg">{m.name}</span>
              <span className="text-xs text-[#4A4F46]">RSVP: {m.rsvp}</span>
            </div>
            <button onClick={() => remove(m.id)} className="text-[#B74134] hover:text-[#8a3025] text-sm flex items-center gap-1" data-testid={`member-delete-${m.id}`}>
              <Trash2 className="w-4 h-4" /> Entfernen
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccommodationsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", distance: "", price_range: "", url: "", phone: "" });

  const load = () => api.get("/accommodations").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.name.trim()) return;
    await api.post("/accommodations", { id: crypto.randomUUID(), ...form });
    setForm({ name: "", description: "", distance: "", price_range: "", url: "", phone: "" });
    await load();
    toast.success("Hinzugefügt");
  };

  const remove = async (id) => {
    await api.delete(`/accommodations/${id}`);
    await load();
  };

  const move = async (id, direction) => {
    await api.post(`/accommodations/${id}/move?direction=${direction}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 grid md:grid-cols-2 gap-4">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="acc-name" />
        <Field label="Entfernung" value={form.distance} onChange={(v) => setForm({ ...form, distance: v })} testid="acc-distance" />
        <Field label="Preisspanne" value={form.price_range} onChange={(v) => setForm({ ...form, price_range: v })} testid="acc-price" />
        <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="acc-phone" />
        <Field label="Webseite (URL)" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testid="acc-url" />
        <div className="md:col-span-2">
          <Field label="Beschreibung" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="acc-desc" multiline />
        </div>
        <div className="md:col-span-2">
          <button onClick={add} className="btn-primary rounded-xl px-5 py-3 inline-flex items-center gap-2" data-testid="acc-add-btn">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={it.id} className="flex items-center justify-between bg-white border border-[#E5DFD3] rounded-xl px-5 py-3">
            <span className="flex items-center gap-2"><Bed className="w-4 h-4 text-[#48634A]" /><span className="font-serif text-lg">{it.name}</span><span className="text-sm text-[#4A4F46]">{it.distance}</span></span>
            <div className="flex items-center gap-2">
              <ReorderButtons onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")} canUp={i > 0} canDown={i < items.length - 1} testidBase={`acc-move-${it.id}`} />
              <button onClick={() => remove(it.id)} className="text-[#B74134] text-sm flex items-center gap-1" data-testid={`acc-delete-${it.id}`}><Trash2 className="w-4 h-4" /> Entfernen</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddressesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", address: "", note: "", video_url: "", video_title: "" });

  const load = () => api.get("/addresses").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.label.trim() || !form.address.trim()) return;
    await api.post("/addresses", { id: crypto.randomUUID(), ...form });
    setForm({ label: "", address: "", note: "", video_url: "", video_title: "" });
    await load();
    toast.success("Hinzugefügt");
  };

  const remove = async (id) => {
    await api.delete(`/addresses/${id}`);
    await load();
  };

  const move = async (id, direction) => {
    await api.post(`/addresses/${id}/move?direction=${direction}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 grid md:grid-cols-2 gap-4">
        <Field label="Bezeichnung" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="addr-label" />
        <Field label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="addr-address" />
        <div className="md:col-span-2">
          <Field label="Hinweis" value={form.note} onChange={(v) => setForm({ ...form, note: v })} testid="addr-note" />
        </div>
        <Field label="Video-Titel (optional)" value={form.video_title} onChange={(v) => setForm({ ...form, video_title: v })} testid="addr-video-title" />
        <Field label="Video-Link (Drive / YouTube / Vimeo / .mp4)" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} testid="addr-video-url" />
        <div className="md:col-span-2">
          <p className="text-xs text-[#4A4F46] italic mb-3">
            Tipp: Bei Google Drive muss der Link auf „Jeder mit dem Link" gestellt sein.
          </p>
          <button onClick={add} className="btn-primary rounded-xl px-5 py-3 inline-flex items-center gap-2" data-testid="addr-add-btn">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((a, i) => (
          <li key={a.id} className="flex items-center justify-between bg-white border border-[#E5DFD3] rounded-xl px-5 py-3">
            <span className="flex items-center gap-2 flex-wrap">
              <MapPin className="w-4 h-4 text-[#48634A]" />
              <span className="font-serif text-lg">{a.label}</span>
              <span className="text-sm text-[#4A4F46]">{a.address}</span>
              {a.video_url && <span className="text-xs text-[#3F6E96] inline-flex items-center gap-1">🎬 Video</span>}
            </span>
            <div className="flex items-center gap-2">
              <ReorderButtons onUp={() => move(a.id, "up")} onDown={() => move(a.id, "down")} canUp={i > 0} canDown={i < items.length - 1} testidBase={`addr-move-${a.id}`} />
              <button onClick={() => remove(a.id)} className="text-[#B74134] text-sm flex items-center gap-1" data-testid={`addr-delete-${a.id}`}><Trash2 className="w-4 h-4" /> Entfernen</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReorderButtons({ onUp, onDown, canUp, canDown, testidBase }) {
  return (
    <div className="inline-flex border border-[#E5DFD3] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onUp}
        disabled={!canUp}
        className="p-1.5 hover:bg-[#F4EFE6] disabled:opacity-30 disabled:cursor-not-allowed text-[#4A4F46]"
        data-testid={`${testidBase}-up`}
        title="Nach oben"
      >
        <ArrowUp className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onDown}
        disabled={!canDown}
        className="p-1.5 hover:bg-[#F4EFE6] disabled:opacity-30 disabled:cursor-not-allowed text-[#4A4F46] border-l border-[#E5DFD3]"
        data-testid={`${testidBase}-down`}
        title="Nach unten"
      >
        <ArrowDown className="w-4 h-4" />
      </button>
    </div>
  );
}

function SecurityManager() {
  const [fam, setFam] = useState("");
  const [adm, setAdm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!fam && !adm) {
      toast.error("Bitte mindestens ein Passwort eingeben");
      return;
    }
    setBusy(true);
    try {
      const res = await api.put("/auth/passwords", {
        family_password: fam || null,
        admin_password: adm || null,
      });
      toast.success(`Aktualisiert: ${res.data.updated.join(", ")}`);
      setFam("");
      setAdm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6">
        <div className="flex items-center gap-2 text-[#48634A] mb-4">
          <Shield className="w-4 h-4" />
          <span className="label-eyebrow">Passwörter ändern</span>
        </div>
        <p className="text-sm text-[#4A4F46] mb-5 leading-relaxed">
          Lass ein Feld leer, wenn du das jeweilige Passwort nicht ändern möchtest.
          Nach dem Speichern bleibst du angemeldet — neue Anmeldungen verwenden das neue Passwort.
        </p>
        <div className="space-y-4">
          <Field label="Neues Familien-Passwort" value={fam} onChange={setFam} testid="security-family" />
          <Field label="Neues Admin-Passwort" value={adm} onChange={setAdm} testid="security-admin" />
        </div>
        <button
          onClick={save}
          disabled={busy || (!fam && !adm)}
          className="btn-primary rounded-xl px-6 py-3 mt-5 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="security-save"
        >
          <KeyRound className="w-4 h-4" /> Passwörter speichern
        </button>
      </div>
      <div className="bg-[#F4EFE6] border border-[#E5DFD3] rounded-2xl p-5 text-sm text-[#4A4F46] leading-relaxed">
        <strong className="text-[#1A1C19]">Tipp:</strong> Notiere dir die neuen Passwörter sofort und versende sie ausschließlich über sichere Kanäle (z. B. persönlich oder via Signal/WhatsApp).
      </div>
    </div>
  );
}

function ExcursionsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", website_url: "", description: "" });
  const [busy, setBusy] = useState({});
  const token = localStorage.getItem("ebel_token");

  const load = () => api.get("/excursions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.name.trim()) return;
    await api.post("/excursions", form);
    setForm({ name: "", website_url: "", description: "" });
    await load();
    toast.success("Ausflugsziel hinzugefügt");
  };

  const update = async (item, patch) => {
    await api.put(`/excursions/${item.id}`, { ...item, ...patch });
    await load();
    toast.success("Gespeichert");
  };

  const remove = async (id) => {
    if (!window.confirm("Ausflugsziel wirklich entfernen?")) return;
    await api.delete(`/excursions/${id}`);
    await load();
  };

  const move = async (id, direction) => {
    await api.post(`/excursions/${id}/move?direction=${direction}`);
    await load();
  };

  const uploadImage = async (id, file) => {
    setBusy((b) => ({ ...b, [id]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/excursions/${id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Bild hinzugefügt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const removeImage = async (excursionId, photoId) => {
    await api.delete(`/excursions/${excursionId}/images/${photoId}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 grid md:grid-cols-2 gap-4">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="exc-name" />
        <Field label="Link zur Webseite" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} testid="exc-url" />
        <div className="md:col-span-2">
          <Field label="Beschreibung (optional)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="exc-desc" multiline />
        </div>
        <div className="md:col-span-2">
          <button onClick={add} className="btn-primary rounded-xl px-5 py-3 inline-flex items-center gap-2" data-testid="exc-add-btn">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
          <p className="text-xs text-[#4A4F46] mt-2 italic">Bilder kannst du in der Liste unten pro Ausflugsziel hochladen (max. 2).</p>
        </div>
      </div>

      <ul className="space-y-4">
        {items.map((it, i) => (
          <li key={it.id} className="bg-white border border-[#E5DFD3] rounded-2xl p-5" data-testid={`exc-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-[#3F7A42]" />
                <span className="font-serif text-xl">{it.name}</span>
                {it.website_url && (
                  <a href={it.website_url} target="_blank" rel="noreferrer" className="text-[#3F6E96] inline-flex items-center gap-1 text-sm">
                    <ExternalLink className="w-3.5 h-3.5" /> Webseite
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ReorderButtons onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")} canUp={i > 0} canDown={i < items.length - 1} testidBase={`exc-move-${it.id}`} />
                <button onClick={() => remove(it.id)} className="text-[#B74134] text-sm flex items-center gap-1" data-testid={`exc-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" /> Entfernen
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <Field label="Name" value={it.name} onChange={(v) => update(it, { name: v })} testid={`exc-name-${it.id}`} />
              <Field label="Link zur Webseite" value={it.website_url || ""} onChange={(v) => update(it, { website_url: v })} testid={`exc-url-${it.id}`} />
              <div className="md:col-span-2">
                <Field label="Beschreibung" value={it.description || ""} onChange={(v) => update(it, { description: v })} testid={`exc-desc-${it.id}`} multiline />
              </div>
            </div>

            <div>
              <p className="label-eyebrow mb-2">Bilder ({it.image_ids?.length || 0} / 2)</p>
              <div className="flex flex-wrap gap-3 items-start">
                {(it.image_ids || []).map((pid) => (
                  <div key={pid} className="relative w-32 h-32 rounded-lg overflow-hidden border border-[#E5DFD3]">
                    <img src={`${API}/photos/${pid}/download?auth=${token}`} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeImage(it.id, pid)}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-[#B74134] hover:text-white rounded-full p-1.5"
                      data-testid={`exc-img-delete-${pid}`}
                      title="Bild entfernen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {(it.image_ids?.length || 0) < 2 && (
                  <label className="w-32 h-32 rounded-lg border-2 border-dashed border-[#E5DFD3] hover:border-[#48634A] flex flex-col items-center justify-center text-[#4A4F46] hover:text-[#48634A] cursor-pointer text-xs gap-1">
                    <Upload className="w-5 h-5" />
                    <span>{busy[it.id] ? "Lädt …" : "Bild hochladen"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={!!busy[it.id]}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadImage(it.id, f);
                        e.target.value = "";
                      }}
                      data-testid={`exc-img-upload-${it.id}`}
                    />
                  </label>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function BackgroundImageEditor({ info, onChange }) {
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("ebel_token");
  const bgId = info.accommodations_bg_id;

  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    fd.append("uploader", "");
    fd.append("tag", "background");
    try {
      const res = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...info, accommodations_bg_id: res.data.id };
      await api.put("/info", updated);
      onChange(res.data.id);
      toast.success("Hintergrund-Bild aktualisiert");
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const removeBg = async () => {
    const updated = { ...info, accommodations_bg_id: "" };
    await api.put("/info", updated);
    onChange("");
    toast.success("Standard-Bild wiederhergestellt");
  };

  return (
    <div>
      <div className="aspect-[16/6] bg-[#F4EFE6] border border-[#E5DFD3] rounded-xl overflow-hidden mb-3">
        {bgId ? (
          <img src={`${API}/photos/${bgId}/download?auth=${token}`} alt="Hintergrund" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#4A4F46] text-sm italic">Standard-Bild aktiv (kein eigenes hochgeladen)</div>
        )}
      </div>
      <label className="inline-flex items-center gap-2 bg-white border border-[#E5DFD3] hover:border-[#48634A] hover:text-[#48634A] rounded-full px-4 py-2 cursor-pointer text-sm" data-testid="bg-upload-label">
        <Upload className="w-4 h-4" />
        {busy ? "Lädt …" : "Neues Hintergrund-Bild hochladen"}
        <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} data-testid="bg-upload-input" />
      </label>
      {bgId && (
        <button onClick={removeBg} className="ml-3 text-sm text-[#B74134] hover:text-[#8a3025] inline-flex items-center gap-1" data-testid="bg-remove">
          <Trash2 className="w-3.5 h-3.5" /> Eigenes Bild entfernen
        </button>
      )}
    </div>
  );
}


function AbrechnungImageEditor({ info, onChange }) {
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("ebel_token");
  const imgId = info.abrechnung_image_id;

  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    fd.append("uploader", "");
    fd.append("tag", "abrechnung");
    try {
      const res = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...info, abrechnung_image_id: res.data.id };
      await api.put("/info", updated);
      onChange(res.data.id);
      toast.success("Screenshot aktualisiert");
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const removeImg = async () => {
    const updated = { ...info, abrechnung_image_id: "" };
    await api.put("/info", updated);
    onChange("");
    toast.success("Screenshot entfernt");
  };

  return (
    <div>
      <div className="bg-[#F4EFE6] border border-[#E5DFD3] rounded-xl overflow-hidden mb-3">
        {imgId ? (
          <img src={`${API}/photos/${imgId}/download?auth=${token}`} alt="Abrechnungs-Übersicht" className="w-full max-h-96 object-contain" />
        ) : (
          <div className="w-full py-10 flex items-center justify-center text-[#4A4F46] text-sm italic">Noch kein Screenshot hochgeladen</div>
        )}
      </div>
      <label className="inline-flex items-center gap-2 bg-white border border-[#E5DFD3] hover:border-[#48634A] hover:text-[#48634A] rounded-full px-4 py-2 cursor-pointer text-sm" data-testid="abrechnung-upload-label">
        <Upload className="w-4 h-4" />
        {busy ? "Lädt …" : "Screenshot hochladen"}
        <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} data-testid="abrechnung-upload-input" />
      </label>
      {imgId && (
        <button onClick={removeImg} className="ml-3 text-sm text-[#B74134] hover:text-[#8a3025] inline-flex items-center gap-1" data-testid="abrechnung-image-remove">
          <Trash2 className="w-3.5 h-3.5" /> Screenshot entfernen
        </button>
      )}
    </div>
  );
}


const KIND_META = {
  login: { label: "Anmeldung", color: "#6B8CAE", tint: "#EEF3F8" },
  member_update: { label: "Mitglieder-Eintrag", color: "#3F7A42", tint: "#EFF3EE" },
  photo_upload: { label: "Bild hochgeladen", color: "#C97B5D", tint: "#FDF1EA" },
  post_messages: { label: "Pinnwand", color: "#6B5BAE", tint: "#EFEBF8" },
  post_guestbook: { label: "Gästebuch", color: "#BE8A4E", tint: "#FBF1E2" },
};

function ActivityPanel() {
  const [dashboard, setDashboard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [notify, setNotify] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadAll = () => {
    api.get("/admin/dashboard").then((r) => setDashboard(r.data));
    api.get("/admin/activity?limit=80").then((r) => setActivity(r.data));
    api.get("/admin/notify-settings").then((r) => setNotify(r.data));
  };
  useEffect(() => {
    loadAll();
  }, []);

  const toggle = async (key) => {
    const next = { ...notify.per_event, [key]: !notify.per_event[key] };
    const res = await api.put("/admin/notify-settings", next);
    setNotify({ ...notify, per_event: res.data.per_event });
    toast.success("Einstellung gespeichert");
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/notify-test");
      toast.success(`Test-Mail an ${r.data.sent_to} gesendet`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="activity-stats">
          <StatPlate icon={<Eye className="w-4 h-4" />} label="Besuche gesamt" value={dashboard.visits.total} accent="#3F6E96" tint="#E0ECF6" />
          <StatPlate icon={<Eye className="w-4 h-4" />} label="Letzte 24 h" value={dashboard.visits.h24} accent="#C97B5D" tint="#FDE8DC" />
          <StatPlate icon={<Eye className="w-4 h-4" />} label="Letzte 7 Tage" value={dashboard.visits.d7} accent="#3F7A42" tint="#DCEADD" />
          <StatPlate icon={<Activity className="w-4 h-4" />} label="Aktionen 7T" value={dashboard.member_updates_7d + dashboard.photos + dashboard.pinnwand_posts + dashboard.guestbook_entries} accent="#6B5BAE" tint="#EFEBF8" />
        </div>
      )}

      {/* Notification toggles */}
      {notify && (
        <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6" data-testid="notify-panel">
          <div className="flex items-center gap-2 text-[#48634A] mb-4">
            <Mail className="w-4 h-4" />
            <span className="label-eyebrow">E-Mail-Benachrichtigungen</span>
          </div>
          {notify.enabled ? (
            <p className="text-sm text-[#4A4F46] mb-4">
              Aktiv. Benachrichtigungen gehen an <strong>{notify.email}</strong>. Du kannst einzelne Ereignistypen aus- und einschalten:
            </p>
          ) : (
            <p className="text-sm text-[#B74134] mb-4">
              Nicht eingerichtet. Bitte <code>RESEND_API_KEY</code> und <code>ADMIN_EMAIL</code> in der Backend-Konfiguration setzen.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.entries(notify.per_event).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={v}
                  onChange={() => toggle(k)}
                  className="w-4 h-4 accent-[#48634A]"
                  data-testid={`notify-${k}`}
                />
                <span className="text-sm">{KIND_META[k]?.label || k}</span>
              </label>
            ))}
          </div>
          <button
            onClick={sendTest}
            disabled={testing || !notify.enabled}
            className="btn-primary rounded-xl px-5 py-2.5 mt-5 inline-flex items-center gap-2 text-sm disabled:opacity-50"
            data-testid="notify-test"
          >
            <Mail className="w-4 h-4" /> {testing ? "Sende …" : "Test-Mail senden"}
          </button>
        </div>
      )}

      {/* Activity feed */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl tracking-tight">Aktivität</h3>
          <button onClick={loadAll} className="text-sm text-[#48634A] hover:text-[#364A38]" data-testid="activity-refresh">Aktualisieren</button>
        </div>
        {activity.length === 0 ? (
          <p className="text-[#4A4F46] italic">Noch keine Aktivität.</p>
        ) : (
          <ol className="space-y-2" data-testid="activity-list">
            {activity.map((a) => {
              const m = KIND_META[a.kind] || { label: a.kind, color: "#4A4F46", tint: "#F4EFE6" };
              return (
                <li key={a.id} className="bg-white border border-[#E5DFD3] rounded-xl p-4 flex gap-4 items-start">
                  <span
                    className="shrink-0 px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wider"
                    style={{ backgroundColor: m.tint, color: m.color }}
                  >
                    {m.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1A1C19] text-sm leading-snug">{a.summary}</p>
                    {a.details && Object.keys(a.details).length > 0 && (
                      <div className="mt-1 text-xs text-[#4A4F46] flex flex-wrap gap-x-3 gap-y-0.5">
                        {Object.entries(a.details).filter(([, v]) => v && v !== "—").map(([k, v]) => (
                          <span key={k}><strong>{k}:</strong> {String(v).slice(0, 80)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-[#4A4F46] whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function StatPlate({ icon, label, value, accent, tint }) {
  return (
    <div className="rounded-2xl overflow-hidden border shadow-[0_6px_24px_-20px_rgba(72,99,74,0.4)]" style={{ borderColor: `${accent}55` }}>
      <div className="px-4 py-2 text-white flex items-center gap-2" style={{ backgroundColor: accent }}>
        {icon}
        <span className="font-semibold uppercase tracking-[0.16em] text-[0.7rem]">{label}</span>
      </div>
      <div className="p-5 text-center" style={{ backgroundColor: tint }}>
        <p className="font-serif text-4xl tracking-tight" style={{ color: accent }}>{value}</p>
      </div>
    </div>
  );
}
